import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Paper,
  Typography,
  InputAdornment,
  Autocomplete,
  Alert,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Stack } from "@mui/system";
import dayjs from "dayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CancelIcon from "@mui/icons-material/Cancel";
import AddCircleOutlinedIcon from "@mui/icons-material/AddCircleOutlined";
import { useDispatch, useSelector } from "react-redux";
import {
  selectUserData,
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../../../../features/userSlice";
import {
  setSelectedAssets,
  setSelectedLabels,
  setSelectedNoteDate,
  setSelectedStakeholders,
} from "../../../../features/activityNotesSlice";
import { ActivityNotesEndPointLogic } from "../../../../logic/AssetManagement/ActivityNotes/ActivityNotesLogic/ActivityNotesEndPointLogic";
import SimpleBackdrop from "../../../BackDrop/BackDrop";
import { useSearchParams } from "react-router-dom";

export const EditNote = ({
  open,
  handleClose,
  activityNote,
  relatedAsset,
}: any) => {
  const dialogContentRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const [searchParams] = useSearchParams();
  const [note, setNote] = useState("");
  const [details, setDetails] = useState(null) as any;
  const [date, setDate] = useState("");
  const [selectedStakeHolder, setSelectedStakeHolder] = useState("");
  const [selectedLabel, setSelectedLabel] = useState([]) as any;
  const [loading, setLoading] = useState(false);
  const [isInputMissing, setIsInputMissing] = useState(false);
  const userData = useSelector(selectUserData);
  const assets: any = userData?.data?.website_permissions?.map((item: any) => {
    const key = Object.keys(item)[0];
    const siteName = item[key].ui_site_name;
    return { label: siteName, value: key };
  });
  const stakeHolder = [{ label: "PPC" }, { label: "BizDev" }];
  const labels = useSelector((state: any) => state.activityNotes.allLabels);
  const labelsContent = labels?.map((item: any) => item?.label);
  const noteShareUid = searchParams.get("noteShareUid");
  const [showLabelInput, setShowLabelInput] = useState(false);
  const [tempSelectedLabels, setTempSelectedLabels] = useState([]) as any;
  const [duplicateLabelMessage, setDuplicateLabelMessage] = useState(""); // State to track duplicate label message

  useEffect(() => {
    if (open && activityNote) {
      setNote(activityNote.note);
      setSelectedStakeHolder(activityNote.stakeholder);
      setSelectedLabel(activityNote.labels);
      setDate(dayjs(activityNote.activity_date).format("YYYY-MM-DD"));
      setDetails(activityNote.details);
    }
  }, [open, activityNote]);

  useEffect(() => {
    if (isInputMissing) {
      const timer = setTimeout(() => {
        setIsInputMissing(false);
      }, 3000);
      return () => clearTimeout(timer); // Clear the timer on component unmount
    }
  }, [isInputMissing]);

  useEffect(() => {
    // Reset scroll position on component mount or dialog open
    if (dialogContentRef?.current) {
      dialogContentRef.current.scrollTop = 0;
    }
    if (isInputMissing && dialogContentRef?.current) {
      dialogContentRef?.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [isInputMissing]);

  const handleEditNote = async () => {
    setLoading(true);
    const updateData = {
      data: {
        note_type: selectedStakeHolder || activityNote?.note_type,
        note: note,
        activity_date: date,
        labels: selectedLabel,
        details: details && details.trim() !== "" ? details : null,
        asset: relatedAsset,
      },
    };
    if (oauthTokenHeader) {
      try {
        await ActivityNotesEndPointLogic.updateActivityNote(
          oauthTokenHeader,
          updateData,
          activityNote?.shared_uid
        );
        await ActivityNotesEndPointLogic.getSingleActivityNoteData(
          oauthTokenHeader,
          noteShareUid,
          dispatch
        );
      } catch (error) {
        dispatch(
          setDisplayErrorSnackBar({
            message: "Unable to edit note",
            type: "error",
          })
        );
      } finally {
        setLoading(false);
        handleClose();
        dispatch(setSelectedAssets([]));
        dispatch(setSelectedStakeholders([]));
        dispatch(setSelectedLabels([]));
        dispatch(setSelectedNoteDate(""));
      }
    }
  };

  const handleCancel = () => {
    handleClose();
    setSelectedStakeHolder("");
    setDate("");
    setNote("");
    setSelectedLabel([]);
  };

  const handleAddNewLabel = () => {
    setShowLabelInput(true);
    setTempSelectedLabels(selectedLabel);
    setDuplicateLabelMessage(""); // Clear any existing message
  };

  const handleConfirmLabels = () => {
    setSelectedLabel(tempSelectedLabels);
    setShowLabelInput(false);
    setDuplicateLabelMessage(""); // Clear any existing message
  };

  const handleCancelLabels = () => {
    setTempSelectedLabels(selectedLabel);
    setShowLabelInput(false);
    setDuplicateLabelMessage(""); // Clear any existing message
  };

  const handleRemoveLabel = (indexToRemove: number) => {
    setSelectedLabel(
      selectedLabel.filter((_: any, index: any) => index !== indexToRemove)
    );
  };

  return (
    <>
      {loading ? (
        <SimpleBackdrop />
      ) : (
        <Dialog
          open={open}
          aria-labelledby="scroll-dialog-title"
          aria-describedby="scroll-dialog-description"
          PaperProps={{
            sx: { width: "900px", maxWidth: "900px", overflowY: "auto" },
          }}
        >
          <Paper
            sx={{
              padding: "24px 24px 20px 24px",
              maxHeight: "80vh",
              overflowY: "auto",
            }}
            ref={dialogContentRef}
          >
            <DialogTitle sx={{ padding: "0px 0px 16px 0px" }}>
              Edit Note
            </DialogTitle>
            {isInputMissing && (
              <Alert
                sx={{
                  height: "50%",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  padding: "0 4px",
                }}
                severity="warning"
              >
                Input missing, Please try again!
              </Alert>
            )}
            <Typography>Details</Typography>
            <DialogContent sx={{ padding: "0" }}>
              <TextField
                required
                size="small"
                color="secondary"
                sx={{ width: "100%", mt: "8px", mb: "16px", padding: "0" }}
                id="filled-size-normal"
                label="Note Title"
                placeholder="Please select an industry from the list."
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <Stack flexDirection="row" gap="16px">
                <Autocomplete
                  options={assets}
                  disabled
                  value={relatedAsset}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Related Asset *"
                      placeholder="Choose Asset from the list."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start"></InputAdornment>
                        ),
                      }}
                      color="secondary"
                    />
                  )}
                  size="small"
                  sx={{ width: "50%" }}
                />
                <Autocomplete
                  disabled
                  options={stakeHolder}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Related Brand"
                      placeholder="Choose Brand from the list."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start"></InputAdornment>
                        ),
                      }}
                      color="secondary"
                    />
                  )}
                  size="small"
                  sx={{ width: "50%" }}
                />
              </Stack>
              <Stack flexDirection="row" gap="16px" mt="16px" mb="16px">
                <Autocomplete
                  options={stakeHolder}
                  disabled
                  value={activityNote?.note_type}
                  onChange={(_, newValue: any) => {
                    setSelectedStakeHolder(newValue ? newValue?.label : null);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Stakeholder *"
                      placeholder="Choose Stakeholder from the list."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start"></InputAdornment>
                        ),
                      }}
                      color="secondary"
                    />
                  )}
                  size="small"
                  sx={{ width: "50%" }}
                />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer
                    components={["DatePicker"]}
                    sx={{ paddingTop: "0", overflow: "visible" }}
                  >
                    <DatePicker
                      className="brandDataDatePicker"
                      slotProps={{
                        textField: {
                          size: "small",
                          color: "secondary",
                          required: true,
                          error: false, // Ensure error state is false
                        },
                      }}
                      format="DD-MM-YYYY"
                      value={date ? dayjs(date) : null}
                      onChange={(date) => {
                        if (date && dayjs(date).isValid()) {
                          const formattedDate =
                            dayjs(date).format("YYYY-MM-DD");
                          setDate(formattedDate);
                        } else {
                          setDate(""); // Reset date if invalid
                        }
                      }}
                      sx={{ width: "100%", minWidth: "418px !important" }}
                      label="Start Date"
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </Stack>
              <TextField
                multiline
                minRows={7}
                size="small"
                color="secondary"
                sx={{ width: "100%", mt: "8px", mb: "16px", padding: "0" }}
                id="filled-size-normal"
                label="Details"
                placeholder="Please enter the details."
                value={details}
                onChange={(e) => setDetails(e.target.value)}
              />
            </DialogContent>
            <Typography sx={{ fontSize: "12px", lineHeight: "20px" }}>
              Fields marked with * are mandatory.
            </Typography>
            <Typography mt="16px" mb="8px">
              Labels
            </Typography>
            {showLabelInput && (
              <Stack
                sx={{
                  animation: "slideDown 0.3s ease-in-out",
                  "@keyframes slideDown": {
                    from: { opacity: 0, transform: "translateY(-10px)" },
                    to: { opacity: 1, transform: "translateY(0)" },
                  },
                }}
                mb="16px"
              >
                <Autocomplete
                  multiple
                  freeSolo
                  id="size-small-outlined-multi"
                  size="small"
                  color="secondary"
                  options={(labelsContent || ["N/A"]).filter(
                    (option: any) => !tempSelectedLabels.includes(option)
                  )}
                  getOptionLabel={(option: any) => option}
                  value={tempSelectedLabels}
                  onChange={(_, newValue: any) => {
                    // Check for duplicates within newValue itself
                    const duplicatesWithinNew = newValue.filter(
                      (item: any, index: number) =>
                        newValue.indexOf(item) !== index
                    );

                    if (duplicatesWithinNew.length > 0) {
                      setDuplicateLabelMessage(
                        `"${duplicatesWithinNew[0]}" already exists!`
                      );
                      // Remove only duplicates within newValue itself
                      const uniqueValues = newValue.filter(
                        (item: any, index: number) =>
                          newValue.indexOf(item) === index
                      );
                      setTempSelectedLabels(uniqueValues);
                      // Clear message after 3 seconds
                      setTimeout(() => {
                        setDuplicateLabelMessage("");
                      }, 3000);
                    } else {
                      setDuplicateLabelMessage("");
                      setTempSelectedLabels(newValue);
                    }
                  }}
                  onInputChange={(_, inputValue, reason) => {
                    // Handle real-time duplicate detection while typing
                    if (reason === "input" && inputValue) {
                      const trimmedInput = inputValue.trim();
                      if (trimmedInput) {
                        // Check if the current input matches any existing labels in tempSelectedLabels
                        const isDuplicateInTemp = tempSelectedLabels.some(
                          (label: string) =>
                            label.toLowerCase() === trimmedInput.toLowerCase()
                        );

                        if (isDuplicateInTemp) {
                          setDuplicateLabelMessage(
                            `Label "${trimmedInput}" already exists!`
                          );
                        } else {
                          setDuplicateLabelMessage("");
                        }
                      } else {
                        setDuplicateLabelMessage("");
                      }
                    } else if (reason === "clear") {
                      setDuplicateLabelMessage("");
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="labels"
                      placeholder="Select or type labels"
                      color="secondary"
                      autoFocus
                      error={!!duplicateLabelMessage}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          const inputElement = e.target as HTMLInputElement;
                          const inputValue = inputElement.value.trim();
                          if (
                            inputValue &&
                            (tempSelectedLabels.includes(inputValue) ||
                              selectedLabel.includes(inputValue))
                          ) {
                            e.preventDefault();
                            setDuplicateLabelMessage(
                              `"${inputValue}" already exists!`
                            );
                            setTimeout(() => {
                              setDuplicateLabelMessage("");
                            }, 3000);
                          }
                        }
                      }}
                    />
                  )}
                  sx={{
                    width: "100%",
                    mb: duplicateLabelMessage ? "4px" : "12px",
                  }}
                  ChipProps={{
                    sx: { backgroundColor: "#7B1FA2", color: "#ffffff" },
                    deleteIcon: (
                      <CancelIcon
                        sx={{ color: "rgba(255, 255, 255, 0.5) !important" }}
                      />
                    ),
                  }}
                  ListboxProps={{
                    sx: { backgroundColor: "#ffffff", color: "#000000" },
                  }}
                  renderOption={(props, option, { selected }) => (
                    <li
                      {...props}
                      style={{
                        backgroundColor: selected ? "#F7EEF9" : "transparent",
                      }}
                    >
                      {option}
                    </li>
                  )}
                />
                {duplicateLabelMessage && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{
                      fontSize: "12px",
                      marginLeft: "14px",
                      marginBottom: "8px",
                      animation: "fadeIn 0.3s ease-in-out",
                      "@keyframes fadeIn": {
                        from: { opacity: 0 },
                        to: { opacity: 1 },
                      },
                    }}
                  >
                    {duplicateLabelMessage}
                  </Typography>
                )}
                <Stack flexDirection="row" gap="8px" justifyContent="flex-end">
                  <Button
                    size="small"
                    color="secondary"
                    onClick={handleCancelLabels}
                    sx={{ textTransform: "none" }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="secondary"
                    onClick={handleConfirmLabels}
                    disabled={
                      tempSelectedLabels.length === 0 || !!duplicateLabelMessage
                    }
                    sx={{ textTransform: "none" }}
                  >
                    Confirm
                  </Button>
                </Stack>
              </Stack>
            )}
            <Stack flexDirection="row" gap="16px" mb="16px" alignItems="center">
              {selectedLabel.length > 0 && !showLabelInput && (
                <Stack flexDirection="row" gap="8px" flexWrap="wrap">
                  {selectedLabel.map((label: any, index: any) => (
                    <Stack
                      key={index}
                      flexDirection="row"
                      alignItems="center"
                      sx={{
                        backgroundColor: "#7B1FA2",
                        color: "#ffffff",
                        padding: "4px 8px 4px 12px",
                        borderRadius: "16px",
                        fontSize: "12px",
                      }}
                    >
                      <Typography sx={{ fontSize: "12px", marginRight: "6px" }}>
                        {label}
                      </Typography>
                      <CancelIcon
                        sx={{
                          fontSize: "16px",
                          cursor: "pointer",
                          color: "rgba(255, 255, 255, 0.7)",
                          "&:hover": {
                            color: "rgba(255, 255, 255, 1)",
                          },
                        }}
                        onClick={() => handleRemoveLabel(index)}
                      />
                    </Stack>
                  ))}
                </Stack>
              )}
              <Button
                variant="outlined"
                disabled={showLabelInput}
                sx={{
                  display: "flex",
                  padding: "4px",
                  alignItems: "center",
                  borderRadius: "100px",
                  border: "1px solid #7B1FA2",
                  color: "#7B1FA2",
                  textTransform: "none",
                  fontSize: "14px",
                  fontWeight: "normal",
                  "&:hover": {
                    border: "1px solid #7B1FA2",
                    backgroundColor: "rgba(123, 31, 162, 0.04)",
                  },
                  "&:disabled": {
                    border: "1px solid #ccc",
                    color: "#ccc",
                  },
                }}
                onClick={handleAddNewLabel}
              >
                <AddCircleOutlinedIcon
                  sx={{
                    marginRight: "8px",
                    color: showLabelInput ? "#ccc" : "#7B1FA2",
                  }}
                />
                Add Labels
              </Button>
            </Stack>
            <DialogActions sx={{ padding: "16px 0 0 0" }}>
              <Button onClick={handleCancel} color="secondary">
                Cancel
              </Button>
              <Button
                onClick={handleEditNote}
                disabled={!note || !date || loading}
                color="secondary"
                variant="contained"
                data-tracker="107"
              >
                Edit Note
              </Button>
            </DialogActions>
          </Paper>
        </Dialog>
      )}
    </>
  );
};
